import React, { useState, useEffect, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Avatar from "react-avatar";
import { UserContext } from "context/user";
import {
  getContactDetails,
  createContactDetails,
  updateContactDetails,
  deleteAccount,
  getStripePortal,
} from "api/User";
import "./index.scss";
import InputField from "components/Input";
import Button from "components/Button";
import DeleteProfileModal from "components/Modal/DeleteAccountProfile";
import AddressFields from "components/AddressFields";
import { getUserTwitterDomain } from "utils";

const navLinks = [
  {
    header: "PROFILE",
    subLinks: [
      {
        linkItem: "Name",
        link: "#name",
        id: "link-name",
      },
      {
        linkItem: "Address",
        link: "#address",
        id: "link-address",
      },
      {
        linkItem: "User Type",
        link: "#user-type",
        id: "link-user-type",
      },
      {
        linkItem: "Photo",
        link: "#photo",
        id: "link-photo",
      },
    ],
  },
  {
    header: "EMAIL SETTINGS",
    subLinks: [
      {
        linkItem: "Email Address",
        link: "#email-address",
        id: "link-email-address",
      },
      // {
      //   linkItem: "News Letter",
      //   link: "#news-letter",
      //   id: "link-news-letter",
      // },
      // {
      //   linkItem: "Updates",
      //   link: "#updates",
      //   id: "link-updates",
      // },
    ],
  },
  // {
  //   header: "SOCIAL CONNECTION",
  //   subLinks: [
  //     {
  //       linkItem: "Facebook",
  //       link: "#facebook",
  //       id: "link-facebook",
  //     },
  //     {
  //       linkItem: "Twitter",
  //       link: "#twitter",
  //       id: "link-twitter",
  //     },
  //     {
  //       linkItem: "Google",
  //       link: "#google",
  //       id: "link-google",
  //     },
  //   ],
  // },
  // {
  //   header: "PREMIUM SUBSCRIPTION",
  //   subLinks: [
  //     {
  //       linkItem: "Subscription Status",
  //       link: "#subscription-status",
  //       id: "link-subscription-status",
  //     },
  //     {
  //       linkItem: "Billing History",
  //       link: "#billing-history",
  //       id: "link-billing-history",
  //     },
  //   ],
  // },
  {
    header: "PAYMENT SETTINGS",
    subLinks: [
      {
        linkItem: "Manage Payment Options",
        link: "#manage-payment-options",
        id: "link-manage-payment-options",
      },
    ],
  },
  {
    header: "SECURITY",
    subLinks: [
      // {
      //   linkItem: "Sign Out Sessions",
      //   link: "#sign-out-session",
      //   id: "link-sign-out-session",
      // },
      {
        linkItem: "Delete Account",
        link: "#delete-account",
        id: "link-delete-account",
      },
    ],
  },
];

const ProfileSettingsContainer = () => {
  const history = useHistory();
  const { token, user } = useContext(UserContext);
  const { logout } = useAuth0();
  const [activeSection, setActiveSection] = useState("link-name");
  const [userDetails, setUserDetails] = useState(null);
  const [formType, setFormType] = useState("");
  const [isEditting, setIsEditting] = useState({
    form: "",
    isEditting: false,
  });
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    userType: "",
    streetAddress: "",
    country: "",
    state: "",
    city: "",
    zipCode: "",
    email: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingEmail, setUpdatingEmail] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [deleteAccountText, setDeleteAccountText] = useState("");
  const [country, setCountry] = useState("");
  const [emailDomain, setEmailDomain] = useState("");

  const handleSelectCountry = (val) => {
    setCountry(val.toUpperCase());
  };

  const fetchData = useCallback(async () => {
    if (!token) return;
    const userDetailsData = await getContactDetails(token);
    setUserDetails(userDetailsData.data);
    if (userDetailsData.data.email) {
      const userDomain = getUserTwitterDomain(userDetailsData.data.email);
      setEmailDomain(userDomain);
    }
  }, [token]);

  useEffect(() => {
    if (userDetails) {
      if (emailDomain !== "twitter.df") {
        setFormValues({
          firstName: userDetails?.first_name || "",
          lastName: userDetails?.last_name || "",
          userType: userDetails?.user_type || "",
          address1: userDetails?.address1 || "",
          address2: userDetails?.address2 || "",
          city: userDetails?.city || "",
          zipCode: userDetails?.post_code || "",
          email: userDetails?.email || user?.email,
          state: userDetails?.state || "",
        });
        setCountry(userDetails?.country);
        setFormType("update");
      } else {
        setFormValues({
          firstName: userDetails?.first_name || "",
          lastName: userDetails?.last_name || "",
          userType: userDetails?.user_type || "",
          address1: userDetails?.address1 || "",
          address2: userDetails?.address2 || "",
          city: userDetails?.city || "",
          zipCode: userDetails?.post_code || "",
          email: "",
          state: userDetails?.state || "",
        });
        setCountry(userDetails?.country);
        setFormType("update");
      }
    } else {
      setFormValues({
        email: userDetails?.email || user?.email,
      });
      setFormType("add");
    }
  }, [userDetails, user, emailDomain]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (event) => {
    event.preventDefault();
    const { value, name } = event.target;
    const newField = { ...formValues, [name]: value };
    setFormValues(newField);
  };

  const handleClickEdit = (section, isEdit) => {
    setIsEditting({
      form: section,
      isEditting: isEdit,
    });
  };

  const handleSaveProfileDetails = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    if (formValues?.email === "") {
      toast.error("Email address is required.");
      setIsSubmitting(false);
    } else {
      const params = {
        data: {
          first_name: formValues?.firstName || "",
          last_name: formValues?.lastName || "",
          phone_number: "",
          email: formValues?.email || user?.email,
          address1: formValues?.address1,
          address2: formValues?.address2,
          city: formValues?.city,
          state: formValues?.state,
          country,
          post_code: formValues?.zipCode,
          user_type: formValues?.userType || "",
        },
      };
      let response = {};
      if (formType === "add") {
        response = await createContactDetails(params, token);
      } else {
        response = await updateContactDetails(params, token);
      }
      setIsSubmitting(false);
      if ([204, 200, 201].includes(response.status)) {
        toast.success("Profile successfully updated");
        history.push("/settings#profile");
      }
      setIsEditting({
        form: "",
        isEditting: false,
      });
    }
  };

  const isEdittingProfile = () => {
    return !!(isEditting?.form === "profile" && isEditting?.isEditting);
  };

  const isEdittingEmail = () => {
    return !!(isEditting?.form === "email" && isEditting?.isEditting);
  };

  const handleUpdateEmail = async (event) => {
    event.preventDefault();
    setUpdatingEmail(true);
    const params = {
      data: {
        first_name: formValues?.firstName || "",
        last_name: formValues?.lastName || "",
        phone_number: "",
        email:
          formValues?.email || emailDomain === "twitter.df"
            ? formValues?.email
            : user?.email,
        address1: formValues?.address1,
        address2: formValues?.address2,
        city: formValues?.city,
        state: formValues?.state,
        country,
        post_code: formValues?.zipCode,
        user_type: formValues?.userType || "",
      },
    };
    const response = await updateContactDetails(params, token);
    setUpdatingEmail(false);
    if ([204, 200, 201].includes(response.status)) {
      toast.success("Email successfully updated");
      history.push("/settings#profile");
      window.location.reload();
    }
    setIsEditting({
      form: "",
      isEditting: false,
    });
  };

  const handleDeleteProfile = async (event) => {
    event.preventDefault();
    setIsDeleting(true);
    const response = await deleteAccount(token);
    setIsDeleting(false);
    if ([204, 200].includes(response.status)) {
      toast.success("Profile successfully deleted");
      logout({ returnTo: window.location.origin });
    }
  };

  const handleManagePaymentOptions = async (event) => {
    event.preventDefault();
    try {
      const response = await getStripePortal(token);
      if (response) {
        window.location.replace(response.portal_url, "_blank");
      }
    } catch (e) {
      toast.error(e);
    }
  };

  return (
    <div data-testid="settings-page" className="settings-page">
      <h1 className="title">Settings</h1>
      <div className="left-panel">
        <ul className="link-list">
          {navLinks.map((item) => (
            <li className="nav-section">
              <p className="nav-header">{item.header}</p>
              {item.subLinks?.map((subLink) => {
                return (
                  <a
                    id={subLink?.id}
                    className={`nav-link ${
                      activeSection === subLink?.id ? "-active" : ""
                    }`}
                    href={subLink?.link}
                    onClick={(e) => {
                      setActiveSection(e.target.id);
                    }}
                  >
                    {subLink?.linkItem}
                  </a>
                );
              })}
            </li>
          ))}
        </ul>
      </div>
      <div className="right-panel">
        <section className="section">
          <h2 id="profile" className="header">
            Profile
          </h2>
          <div id="name" className="field-section">
            <p className="section-label">Name</p>
            <p className="details">
              Your name appears on your avatar, as your byline, and in your
              responses. It is a required field.
            </p>
            <div className="input-group">
              <InputField
                label="First Name"
                id="firstName"
                value={formValues?.firstName}
                onChange={(e) => handleInputChange(e)}
                disabled={!isEdittingProfile()}
              />
              <InputField
                label="Last Name"
                id="lastName"
                value={formValues?.lastName}
                onChange={(e) => handleInputChange(e)}
                disabled={!isEdittingProfile()}
              />
            </div>
          </div>

          <div id="address" className="field-section">
            <AddressFields
              user={formValues}
              country={country}
              handleSelectCountry={handleSelectCountry}
              isDisabled={!isEdittingProfile()}
              handleInputChange={handleInputChange}
              isOptional
            />
            <div id="user-type" className="field-section">
              <div className="input-field">
                <select
                  id="userType"
                  name="userType"
                  className="select-input"
                  onChange={(e) => handleInputChange(e)}
                  value={formValues?.userType}
                  disabled={!isEdittingProfile()}
                >
                  <option value="">--</option>
                  <option value="farmer">Farmer</option>
                  <option value="agronomist">Agronomist</option>
                  <option value="researcher">Researcher</option>
                  <option value="input_supplier">Input Supplier</option>
                  <option value="education">Education</option>
                  <option value="other">Other</option>
                </select>
                <label htmlFor="userType" className="label">
                  User Type
                </label>
              </div>
            </div>
            {isEdittingProfile() ? (
              <Button
                onClick={(e) => handleSaveProfileDetails(e)}
                label="Save"
                type="solid"
                color="blue"
                loading={isSubmitting}
              />
            ) : (
              <Button
                onClick={() => handleClickEdit("profile", true)}
                label="Edit"
                type="outlined"
                color="white"
              />
            )}
          </div>

          <div id="photo" className="field-section">
            <p className="section-label">Photo</p>
            <div className="photo-group">
              <p className="details">
                Your photo appears in your avatar, farms collaborated with, etc.
                Recommended size: Square, at least 1000 pixels per side. File
                type: JPG or PNG.
              </p>
              <div className="avatar">
                <Avatar
                  name={user?.email}
                  size="70"
                  src={user?.picture || ""}
                  round
                />
              </div>
            </div>
            {/* for later use */}
            {/* <Button
              onClick={() => {}}
              label="Edit"
              type="outlined"
              color="white"
            /> */}
          </div>
        </section>
        <section className="section">
          <p className="header">Email Settings</p>
          <div id="email-address" className="field-section">
            <InputField
              label="Email Address"
              id="email"
              placeholder="Email Address"
              value={formValues?.email}
              onChange={(e) => handleInputChange(e)}
            />
            {isEdittingEmail() ? (
              <Button
                onClick={(e) => handleUpdateEmail(e)}
                label="Save"
                type="solid"
                color="blue"
                loading={isUpdatingEmail}
              />
            ) : (
              <Button
                onClick={() => handleClickEdit("email", true)}
                label="Change Email Address"
                type="outlined"
                color="white"
              />
            )}
          </div>

          {/* FOR LATER USE */}
          {/* <div id="news-letter" className="field-group">
            <label className="input-label">News Letter</label>
          </div>

          <div id="updates" className="field-group">
            <label className="input-label">Updates from DataFarming</label>
            <div className="toggle">
              <CheckBox onChange={() => {}} />
              <p>New product features on Datafarming.</p>
            </div>
            <div className="toggle">
              <CheckBox onChange={() => {}} />
              <p>Information about advanced plan.</p>
            </div>
            <div className="toggle">
              <CheckBox onChange={() => {}} />
              <p>Updates and announcements from Datafarming.</p>
            </div>
          </div> */}
        </section>

        {/* FOR LATER USE */}
        {/* <div className="section">
          <p className="header">Social Connections</p>
          <>
            <p id="facebook" className="section-label">
              Connect to Facebook
            </p>
            <p className="details">
              We will never post to Facebook or message your friends without
              your permission.
            </p>
            <Button
              onClick={() => {}}
              label="Connect to Facebook"
              type="solid"
              customClassName="facebook"
              logoSrc={fbLogo}
            />
          </>

          <>
            <p id="twitter" className="section-label">
              Connect to Twitter
            </p>
            <p className="details">
              We will never post to Facebook or message your friends without
              your permission.
            </p>
            <Button
              onClick={() => {}}
              label="Connect to Twitter"
              customClassName="twitter"
              type="solid"
              logoSrc={twitterLogo}
            />
          </>

          <>
            <p id="google" className="section-label">
              Connect to Google
            </p>
            <p className="details">
              You can now sign in to Datafarming using your Google account.
            </p>
            <Button
              onClick={() => {}}
              customClassName="google"
              label="Connect to Google"
              type="solid"
              logoSrc={googleLogo}
            />
          </>
        </div> */}
        {/* <div className="section">
          <p className="header">Advanced Subscription</p>
          <div className="field-group">
            <p id="subscription-status" className="section-label">
              Subscribed to Advanced since May 2022
            </p>
            <p className="details">
              You’ll be charged $50/year. Cancel anytime by clicking the cancel
              subscription below.
            </p>
            <Button
              onClick={() => {}}
              label="Manage Subscription"
              color="red"
              type="outlined"
            />
          </div>

          <div className="field-group">
            <p id="billing-history" className="section-label">
              Billing History
            </p>
            <p className="details">
              View details of your Advanced subscription payments.
            </p>
            <Button
              onClick={() => {}}
              label="View Billing History"
              // color="red"
              type="outlined"
            />
          </div>
        </div> */}
        <div className="section">
          <p id="manage-payment-options" className="header">
            Payment Settings
          </p>
          <div className="field-group">
            {/* <p id="subscription-status" className="section-label">
              Subscribed to Advanced since May 2022
            </p> */}
            <p className="details">
              View the details of your premium subscription, update your payment
              method and view your transaction history.
            </p>
            <Button
              onClick={(e) => handleManagePaymentOptions(e)}
              label="Manage Payment Options"
              color="white"
              type="outlined"
            />
          </div>

          {/* <div className="field-group">
            <p id="billing-history" className="section-label">
              Billing History
            </p>
            <p className="details">
              View details of your Advanced subscription payments.
            </p>
            <Button
              onClick={() => {}}
              label="View Billing History"
              // color="red"
              type="outlined"
            />
          </div> */}
        </div>
        <section className="section">
          <p className="header">Security</p>
          {/* FOR LATER USE */}
          {/* <div className="field-group">
            <p id="sign-out-session" className="section-label">
              Sign out of all other sessions
            </p>
            <p className="details">
              This will sign you out of sessions in other browsers or on other
              computers.
            </p>
            <Button
              onClick={() => {}}
              label="Sign out other sessions"
              type="outlined"
            />
          </div> */}

          <div className="field-section">
            <p id="delete-account" className="section-label">
              Delete Account
            </p>
            <p className="details">
              Permanently delete your account and all of your content.
            </p>
            <Button
              onClick={() => setIsOpenDeleteModal(true)}
              label="Delete Account"
              color="red"
              type="solid"
              loading={isDeleting}
            />
          </div>
        </section>
      </div>

      <DeleteProfileModal
        isOpen={isOpenDeleteModal}
        onRequestClose={() => setIsOpenDeleteModal(false)}
        onClose={() => setIsOpenDeleteModal(false)}
        buttonAction={(e) => handleDeleteProfile(e)}
        setDeleteAccount={setDeleteAccountText}
        deleteValue={deleteAccountText}
        data-testid="delete-account-modal"
      />
    </div>
  );
};

ProfileSettingsContainer.propTypes = {};
export default ProfileSettingsContainer;
